/**
 * The four still on their feet in front of the rig, as numbers.
 *
 * `Disco.ts`'s opposite number and its neighbour in every other way: that file
 * is when the lamps fire, this one is when the people in front of them leave
 * the ground. Same split as the rest of `src/render` - `World` owns the
 * figures, this owns what they are doing at render second `t` - and the same
 * pure function of wall-clock time, so a party that has been going since the
 * page opened cannot drift, cannot accumulate, and cannot be got out of step
 * by a tab that spent a minute in the background.
 *
 * THE RATE IS THE RECORD'S, THE BAR IS NOT. `DANCE.beat` is measured off
 * `teltta-dj.m4a` rather than chosen, so a crowd bouncing to it is bouncing at
 * the speed of the thing coming out of the cabinets - which is the half of
 * this anybody watching from the yard can actually check. What is *not*
 * matched is the phase, and it cannot honestly be: the set starts when the run
 * starts and this clock started when the page loaded, so there is no offset
 * between the two to know. Syncing to it would mean the crowd landing on a
 * kick drum the player may have muted, in a set that stops dead the moment the
 * cable comes out of the tolppa - see `World.renderCrowd`, which is where the
 * blackout is answered instead.
 *
 * A HOP IS BALLISTIC AND A HAND IS NOT, and that is the whole difference
 * between the two curves below. A body that has left the grass is under
 * gravity and comes back down on gravity's schedule, with the rest of the beat
 * spent stood on it; a hand never leaves anything, so it swings the whole beat
 * and is never still. One of the two is an event and the other is a motion,
 * and drawing both as the same sine would lose the part that reads as jumping.
 */

import { DANCE } from '../config/gameplay';

/**
 * How one person in the crowd differs from the rest of it. Authored once, at
 * load, and never stepped - the same allowance `plumeSeeds` is written for.
 *
 * Two numbers and no more, because they are the two a crowd is told apart by
 * from six metres away: nobody lands on the beat exactly, and nobody is going
 * at it quite as hard as the man beside them.
 */
export interface DancerSeed {
  /** How far their own beat sits off the record's, in seconds. Signed. */
  offset: number;
  /** How hard they are going, around 1. Scales the hop and the arms alike. */
  vigour: number;
}

/**
 * The whole crowd at once, spaced across the beat and then nudged.
 *
 * Spaced rather than rolled, which is the same trick `plumeSeeds` uses on its
 * queue and it is here for a sharper reason: four offsets drawn independently
 * can land on top of each other, and two men in a crowd of four hitting the
 * grass on the same millisecond is a pair of dancers who look wired together.
 * Giving each of them their own share of the window and rolling *inside* it
 * makes that impossible however the stream falls. The vigour is a plain draw,
 * because it is the one number here nobody can read off a neighbour.
 *
 * `rng` is expected to be a seeded generator called at load time and never
 * again - the same allowance `src/utils/prng.ts` is written for. Nothing about
 * the crowd is random once the level is built.
 */
export function dancerSeeds(count: number, rng: () => number): DancerSeed[] {
  const seeds: DancerSeed[] = [];
  for (let i = 0; i < count; i++) {
    seeds.push({
      offset: ((i + rng()) / count - 0.5) * DANCE.spread * DANCE.beat,
      vigour: 1 + (rng() - 0.5) * DANCE.vary,
    });
  }
  return seeds;
}

/** How high this one gets, in metres. */
function apex(seed: DancerSeed): number {
  return DANCE.hop * seed.vigour;
}

/**
 * How long they are off the ground for, in seconds.
 *
 * Not a tuning value: a body thrown to `apex` under `DANCE.gravity` is in the
 * air for exactly this long, so the man who jumps higher hangs longer without
 * anybody having to say so. That is also what keeps a taller hop from quietly
 * eating the whole beat - `Dance.test.ts` holds every dancer's feet on the
 * grass for part of every one.
 */
export function airtime(seed: DancerSeed): number {
  return 2 * Math.sqrt((2 * apex(seed)) / DANCE.gravity);
}

/** Seconds into this dancer's own beat, 0..`DANCE.beat`. */
function age(seed: DancerSeed, renderTime: number): number {
  const beat = DANCE.beat;
  // Normalised rather than trusted: `%` on a negative is negative in JS, and
  // half the offsets here are.
  return (((renderTime - seed.offset) % beat) + beat) % beat;
}

/**
 * How far off the grass they are this frame, in metres.
 *
 * A parabola for the length of the hop and a flat zero for the rest of the
 * beat. The zero is the load-bearing half: a crowd that is in the air all the
 * time is a crowd floating, and it is the frames with both feet down - the
 * landing, and the crouch that follows it - that make the other ones read as a
 * jump at all.
 */
export function jumpHeight(seed: DancerSeed, renderTime: number): number {
  const air = airtime(seed);
  const at = age(seed, renderTime);
  if (at >= air) return 0;
  // 4h·u(1-u): up, over and down again, which is what constant gravity gives.
  const u = at / air;
  return 4 * apex(seed) * u * (1 - u);
}

/**
 * How far *below* the pose `World.raiseArms` built the hand is being carried
 * right now, in radians. Never above it, and that is deliberate.
 *
 * The raised pose is the top of the swing rather than the middle of it. An arm
 * already held over its owner's head has nowhere above it to go: another
 * thirty degrees of lift puts the hand behind the man, which is a stretch
 * rather than a cheer. So the pump is what the hands do on the way *down* from
 * where they already are, and the top of every swing is the pose the figure
 * was built in. The blackout stops this swing and lowers the arms instead.
 *
 * Zero at the top of the hop and deepest half a beat later, at the bottom of
 * the crouch. The two are one gesture: hands and heels come off the ground
 * together, because that is what jumping with your arms up *is*.
 */
export function armDrop(seed: DancerSeed, renderTime: number): number {
  const phase = (age(seed, renderTime) - airtime(seed) / 2) / DANCE.beat;
  // A cosine rather than the hop's own parabola: the hand is swung rather than
  // thrown, so it is moving on every frame of the beat instead of hanging at
  // the bottom of it waiting for the feet.
  return DANCE.pump * seed.vigour * (1 - Math.cos(phase * Math.PI * 2)) * 0.5;
}
